/**
 * @component ry-split
 * @category  layout
 * @summary   Two-pane splitter (ry-ui-only). Pane A is the first child, pane B the last. By default they split 50/50; set `[ratio="a/b"]` for a proportional split, or `[size]` to fix one pane (px/%) while the other fills. Choose the axis with `[orientation]`, which pane `[size]` targets with `[primary]`, and the gutter with `[gap]`. Add `[resizable]` for a drag handle (mouse/touch/keyboard, double-click reset), `[persist="key"]` to remember the size, `[divided]` for a divider line. Nest splits (horizontal inside vertical, etc.) for panel layouts.
 *
 * @attribute orientation  'horizontal' | 'vertical'   default='horizontal'   Split axis. Horizontal = side-by-side; vertical = stacked. Handle + resize follow the axis.
 * @attribute primary      'start' | 'end'             default='end'          Which pane `[size]` fixes (only relevant in size mode). `end` (default) = pane B (right/bottom) is fixed, pane A fills — the classic right-rail. `start` = pane A (left/top) is fixed. No effect on the default 50/50 or `[ratio]` modes.
 * @attribute size         string?                     Size of the PRIMARY pane: a pixel value (`"280"` / `"280px"`) or percentage (`"30%"`). The other pane fills. Drives `--ry-split-size`. Ignored when `[ratio]` is set.
 * @attribute ratio        string?                     Proportional split, `"a/b"` or `"a:b"` (e.g. `"2/1"`, `"3:2"`). Both panes share space by flex-grow `a : b`; overrides `[size]`. On first drag of a `[resizable]` ratio split, it converts to a fixed `px` size.
 * @attribute gap          'none' | 'sm' | 'md' | 'lg'  default=(1.5rem)      Gutter between panes. `none` = flush/unstyled. Drives `--ry-split-gap`.
 * @attribute divided      boolean   default=false     Zero the gap and draw a 1px divider line between the panes (theme color) for visual depth.
 * @attribute resizable    boolean   default=false     Inserts a drag handle between the panes; enables mouse/touch/keyboard resize. Without it the split is pure CSS layout.
 * @attribute persist      string?                     localStorage key. With `[resizable]`, the primary size is restored from `localStorage["ry-split:<key>"]` at mount and saved on every resize commit. Ignored without `[resizable]`.
 * @attribute no-collapse  boolean   default=false     By default a horizontal split auto-stacks to a column at `@container (max-width: 768px)`. Set `[no-collapse]` to keep it side-by-side at all widths.
 *
 * @property  size  number   Read-only — current primary-pane size in px (`getBoundingClientRect()` along the split axis).
 *
 * @event ry:resize
 *        detail: { size: number, width: number }
 *        fires:  on every resize commit — drag end, each keyboard step, double-click reset. `size` is the primary pane's size in px along the axis; `width` is a backward-compat alias.
 *
 * @keyboard  When the handle is focused (`tabindex="0"`). Horizontal: ArrowLeft / ArrowRight resize (direction depends on `[primary]`). Vertical: ArrowUp / ArrowDown. Shift = 50px step (else 10px). Home = min, End = max.
 *
 * @state     `data-ry-resizing` set on the host during an active drag.
 *
 * @content   Exactly TWO direct children (the panes). The handle is inserted between them when `[resizable]`.
 *
 * @dom ([resizable])
 *   ry-split[orientation?][primary?][resizable][data-ry-resizing?]
 *     > {pane A}
 *     > div.ry-split__handle   [data-ry-target="handle"]   role="separator"   aria-orientation=…   tabindex="0"
 *     > {pane B}
 *
 * @gotcha  Default is 50/50. Sizing knobs (size mode only): `--ry-split-size` (set by `[size]`, drag, or restore; legacy `--ry-split-width` is bridged), `--ry-split-min` (default 100px; alias `--ry-split-min-width`), `--ry-split-max` (default 80% of the container along the axis; alias `--ry-split-max-width`). Min/max are clamped by drag + keyboard.
 *
 * @gotcha  `[primary="end"]` (the default) sizes pane B — so dragging the handle TOWARD the start grows it (drag left in a horizontal split, up in a vertical one). `[primary="start"]` sizes pane A and inverts that. This is the inverted-axis right-rail idiom.
 *
 * @gotcha  `[ratio]` and `[size]` are mutually exclusive — `[ratio]` wins. A `[resizable]` ratio split converts to a fixed `px` size on the first drag (the `ratio` attribute is removed), because you can't drag a pure proportion.
 *
 * @gotcha  Completely unstyled: with `gap="none"` and `data-ry-theme="none"` the split is pure flush layout — the handle keeps its 8px hit area but has no visible bar (the indicator is a theme concern). Add `[divided]` for a 1px rule between panes.
 *
 * @gotcha  `[persist]` is ignored without `[resizable]`. Double-click on the handle RESETS — it removes the inline size AND clears the persisted value.
 *
 * @gotcha  Drag needs real layout (getBoundingClientRect), untestable in happy-dom. Vitest covers mount + handle + ARIA + keyboard + persistence; cover drag visually with Playwright.
 *
 * @example default-50-50
 * ```html
 * <!-- No attributes = an even 50/50 split. -->
 * <ry-split>
 *   <ry-card>Left half</ry-card>
 *   <ry-card>Right half</ry-card>
 * </ry-split>
 * ```
 *
 * @example fixed-right-rail
 * ```html
 * <!-- Fix pane B (the right rail) to 280px; pane A fills. -->
 * <ry-split size="280px">
 *   <main>Main content</main>
 *   <aside>Sidebar</aside>
 * </ry-split>
 * ```
 *
 * @example fixed-left-sidebar
 * ```html
 * <ry-split primary="start" size="240px">
 *   <nav>240px nav</nav>
 *   <main>Fills the rest</main>
 * </ry-split>
 * ```
 *
 * @example ratio
 * ```html
 * <!-- 2 : 1 proportional split. -->
 * <ry-split ratio="2/1" style="height:160px">
 *   <ry-card>Two parts</ry-card>
 *   <ry-card>One part</ry-card>
 * </ry-split>
 * ```
 *
 * @example vertical
 * ```html
 * <ry-split orientation="vertical" size="40%" style="height:240px">
 *   <ry-card>Top (40%)</ry-card>
 *   <ry-card>Bottom fills</ry-card>
 * </ry-split>
 * ```
 *
 * @example resizable-persisted
 * ```html
 * <ry-split resizable persist="docs-panel" style="height:200px">
 *   <main>Docs</main>
 *   <aside>Outline — drag to resize, double-click to reset</aside>
 * </ry-split>
 * ```
 *
 * @example divided-flush
 * ```html
 * <ry-split divided style="height:160px">
 *   <ry-card>Flush, with a divider line</ry-card>
 *   <ry-card>No gap</ry-card>
 * </ry-split>
 * ```
 *
 * @example nested-panels
 * ```html
 * <!-- Vertical outer, horizontal inner — an IDE-style panel layout. Give the
 *      OUTER vertical split a ratio so the bottom pane doesn't default to 300px
 *      (which would exceed a short container and squash the editor row). -->
 * <ry-split orientation="vertical" ratio="2/1" resizable style="height:320px">
 *   <ry-split resizable>
 *     <main>Editor</main>
 *     <aside>Inspector</aside>
 *   </ry-split>
 *   <section>Terminal</section>
 * </ry-split>
 * ```
 *
 * @tutorial non-resizable-mounts-without-handle
 * ```js
 * const el = mount('<ry-split><div id="m">m</div><div id="a">a</div></ry-split>');
 * await expectEqual(el.querySelector('[data-ry-target="handle"]'), null, 'no handle without [resizable]');
 * await expectEqual(el.children.length, 2, 'two panes, no handle');
 * ```
 *
 * @tutorial resizable-inserts-handle-between-panes
 * ```js
 * const el = mount('<ry-split resizable><div id="m">m</div><div id="a">a</div></ry-split>');
 * await expectEqual(el.children.length, 3, 'handle inserted');
 * await expectEqual(el.children[1], el.querySelector('[data-ry-target="handle"]'), 'handle is the middle child');
 * ```
 *
 * @tutorial handle-aria-matches-orientation
 * ```js
 * // Horizontal split → vertical separator; vertical split → horizontal separator.
 * const h = mount('<ry-split resizable><div>a</div><div>b</div></ry-split>');
 * await expectAttr(h.querySelector('[data-ry-target="handle"]'), 'aria-orientation', 'vertical');
 * const v = mount('<ry-split resizable orientation="vertical"><div>a</div><div>b</div></ry-split>');
 * await expectAttr(v.querySelector('[data-ry-target="handle"]'), 'aria-orientation', 'horizontal');
 * ```
 *
 * @tutorial size-attribute-sets-css-var
 * ```js
 * const el = mount('<ry-split size="280"><div>a</div><div>b</div></ry-split>');
 * await expectEqual(el.style.getPropertyValue('--ry-split-size'), '280px', 'unit-less size gets px');
 * const pct = mount('<ry-split size="30%"><div>a</div><div>b</div></ry-split>');
 * await expectEqual(pct.style.getPropertyValue('--ry-split-size'), '30%', 'percentage passed through');
 * ```
 *
 * @tutorial ratio-attribute-sets-flex-vars
 * ```js
 * const el = mount('<ry-split ratio="2/1"><div>a</div><div>b</div></ry-split>');
 * await expectEqual(el.style.getPropertyValue('--ry-split-ratio-a'), '2');
 * await expectEqual(el.style.getPropertyValue('--ry-split-ratio-b'), '1');
 * ```
 *
 * @tutorial double-click-resets-size
 * ```js
 * const el = mount('<ry-split resizable><div>m</div><div>a</div></ry-split>');
 * el.style.setProperty('--ry-split-size', '500px');
 * const handle = el.querySelector('[data-ry-target="handle"]');
 * handle.dispatchEvent(new MouseEvent('dblclick', { bubbles: true }));
 * await expectEqual(el.style.getPropertyValue('--ry-split-size'), '', 'inline size cleared');
 * ```
 *
 * @tutorial persist-restores-on-mount
 * ```js
 * localStorage.setItem('ry-split:restore-key', '420');
 * const el = mount('<ry-split resizable persist="restore-key"><div>m</div><div>a</div></ry-split>');
 * await expectEqual(el.style.getPropertyValue('--ry-split-size'), '420px', 'restored from storage');
 * ```
 *
 * @tutorial persist-ignored-without-resizable
 * ```js
 * localStorage.setItem('ry-split:no-rz', '300');
 * const el = mount('<ry-split persist="no-rz"><div>m</div><div>a</div></ry-split>');
 * await expectEqual(el.style.getPropertyValue('--ry-split-size'), '', 'not restored without [resizable]');
 * await expectEqual(el.querySelector('[data-ry-target="handle"]'), null);
 * ```
 *
 * @see ry-stack
 * @see ry-image-comparer
 */

import { RyElement } from '../core/ry-element.js';

const HAS_CSS_UNIT = /^[+-]?\d*\.?\d+([a-zA-Z]+|%)$/;
const IS_NUMBER = /^[+-]?\d*\.?\d+$/;

export class RySplit extends RyElement {
  static get observedAttributes(): string[] {
    return ['size', 'ratio', 'orientation', 'primary'];
  }

  #handle: HTMLElement | null = null;
  #dragging = false;
  #startPos = 0;
  #startSize = 0;

  setup(): void {
    this.#applySizing();

    if (!this.hasAttribute('resizable')) return;

    this.#restore();

    this.#handle = document.createElement('div');
    this.#handle.setAttribute('data-ry-target', 'handle');
    this.#handle.className = 'ry-split__handle';
    this.#handle.setAttribute('role', 'separator');
    this.#handle.setAttribute('aria-orientation', this.#isVertical() ? 'horizontal' : 'vertical');
    this.#handle.setAttribute('tabindex', '0');

    // Insert between the two panes (always before the last child).
    const last = this.lastElementChild;
    if (last) this.insertBefore(this.#handle, last);

    this.on(this.#handle, 'mousedown', this.#onMouseDown);
    this.on(this.#handle, 'touchstart', this.#onTouchStart as EventListener);
    this.on(this.#handle, 'keydown', this.#onKeyDown);
    this.on(this.#handle, 'dblclick', this.#onDoubleClick);
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (!this.hasAttribute('data-ry-initialized')) return;
    if (name === 'size' || name === 'ratio') this.#applySizing();
    if (name === 'orientation' && this.#handle) {
      this.#handle.setAttribute('aria-orientation', this.#isVertical() ? 'horizontal' : 'vertical');
    }
  }

  get size(): number {
    return Math.round(this.#currentSize());
  }

  // ── Sizing from attributes ──
  // Default (no [size]/[ratio]) is a 50/50 ratio split — the CSS falls back to
  // ratio 1:1. [ratio] sets proportional; [size] (or legacy --ry-split-width)
  // sizes one pane and the other fills.
  #applySizing(): void {
    const ratio = this.getAttribute('ratio');
    if (ratio) {
      const [a, b] = ratio.split(/[/:]/).map((n) => parseFloat(n.trim()));
      this.style.setProperty('--ry-split-ratio-a', String(Number.isFinite(a) ? a : 1));
      this.style.setProperty('--ry-split-ratio-b', String(Number.isFinite(b) ? b : 1));
      return;
    }
    const size = this.getAttribute('size');
    if (size != null && size !== '') {
      const raw = size.trim();
      const value = HAS_CSS_UNIT.test(raw) ? raw : IS_NUMBER.test(raw) ? `${raw}px` : raw;
      this.style.setProperty('--ry-split-size', value);
      return;
    }
    // Legacy bridge: an inline --ry-split-width (no [size] attr) still sizes the
    // pane. Mirror it to --ry-split-size and switch to size mode.
    const legacy = this.style.getPropertyValue('--ry-split-width').trim();
    if (legacy) {
      this.style.setProperty('--ry-split-size', legacy);
      this.setAttribute('data-ry-sized', '');
    }
  }

  // ── Geometry ──
  #isVertical(): boolean {
    return this.getAttribute('orientation') === 'vertical';
  }

  #primaryStart(): boolean {
    return this.getAttribute('primary') === 'start';
  }

  /** The sized/resizable pane element. */
  #sizedPane(): HTMLElement | null {
    return (this.#primaryStart() ? this.firstElementChild : this.lastElementChild) as HTMLElement | null;
  }

  #currentSize(): number {
    const pane = this.#sizedPane();
    if (!pane) return parseFloat(getComputedStyle(this).getPropertyValue('--ry-split-size')) || 300;
    const r = pane.getBoundingClientRect();
    return this.#isVertical() ? r.height : r.width;
  }

  #readVar(name: string, alias: string, fallback: number): number {
    const cs = getComputedStyle(this);
    const v = cs.getPropertyValue(name).trim() || cs.getPropertyValue(alias).trim();
    return v ? parseFloat(v) : fallback;
  }

  #min(): number {
    return this.#readVar('--ry-split-min', '--ry-split-min-width', 100);
  }

  #max(): number {
    const explicit = getComputedStyle(this).getPropertyValue('--ry-split-max').trim()
      || getComputedStyle(this).getPropertyValue('--ry-split-max-width').trim();
    if (explicit) return parseFloat(explicit);
    const r = this.getBoundingClientRect();
    return (this.#isVertical() ? r.height : r.width) * 0.8;
  }

  #setSize(px: number): void {
    const clamped = Math.round(Math.max(this.#min(), Math.min(this.#max(), px)));
    this.style.setProperty('--ry-split-size', `${clamped}px`);
    // A committed pixel size switches the split from ratio (default 50/50) to
    // fixed-pane mode.
    this.setAttribute('data-ry-sized', '');
  }

  // ── Pointer drag ──
  #onMouseDown = (e: MouseEvent): void => {
    e.preventDefault();
    this.#startDrag(this.#isVertical() ? e.clientY : e.clientX);
    const onMove = (ev: MouseEvent) => this.#onDrag(this.#isVertical() ? ev.clientY : ev.clientX);
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      this.#endDrag();
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  #onTouchStart = (e: TouchEvent): void => {
    const touch = e.touches[0];
    if (!touch) return;
    e.preventDefault();
    this.#startDrag(this.#isVertical() ? touch.clientY : touch.clientX);
    const onMove = (ev: TouchEvent) => {
      const t = ev.touches[0];
      if (t) this.#onDrag(this.#isVertical() ? t.clientY : t.clientX);
    };
    const onEnd = () => {
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      this.#endDrag();
    };
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
  };

  #startDrag(pos: number): void {
    // A ratio split can't be dragged as a proportion — convert to a fixed px
    // size on first grab, then drag normally.
    if (this.hasAttribute('ratio')) {
      const px = this.#currentSize();
      this.removeAttribute('ratio');
      this.style.removeProperty('--ry-split-ratio-a');
      this.style.removeProperty('--ry-split-ratio-b');
      this.#setSize(px);
    }
    this.#dragging = true;
    this.#startPos = pos;
    this.#startSize = this.#currentSize();
    this.setAttribute('data-ry-resizing', '');
    document.body.style.cursor = this.#isVertical() ? 'row-resize' : 'col-resize';
    document.body.style.userSelect = 'none';
  }

  #onDrag(pos: number): void {
    if (!this.#dragging) return;
    // Pane A (primary=start) grows toward the end; pane B (primary=end) grows
    // toward the start.
    const delta = this.#primaryStart() ? pos - this.#startPos : this.#startPos - pos;
    this.#setSize(this.#startSize + delta);
  }

  #endDrag(): void {
    this.#dragging = false;
    this.removeAttribute('data-ry-resizing');
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    this.#persist();
    this.#emitResize();
  }

  // ── Keyboard ──
  #onKeyDown = (e: KeyboardEvent): void => {
    const step = e.shiftKey ? 50 : 10;
    const size = this.#currentSize();
    const grow = this.#primaryStart() ? step : -step; // start grows toward end key
    const vertical = this.#isVertical();
    const growKey = vertical ? 'ArrowDown' : 'ArrowRight';
    const shrinkKey = vertical ? 'ArrowUp' : 'ArrowLeft';

    if (e.key === growKey) {
      e.preventDefault();
      this.#setSize(size + grow);
      this.#persist();
      this.#emitResize();
    } else if (e.key === shrinkKey) {
      e.preventDefault();
      this.#setSize(size - grow);
      this.#persist();
      this.#emitResize();
    } else if (e.key === 'Home') {
      e.preventDefault();
      this.#setSize(this.#min());
      this.#persist();
      this.#emitResize();
    } else if (e.key === 'End') {
      e.preventDefault();
      this.#setSize(this.#max());
      this.#persist();
      this.#emitResize();
    }
  };

  #onDoubleClick = (): void => {
    this.style.removeProperty('--ry-split-size');
    this.style.removeProperty('--ry-split-width'); // legacy alias
    this.removeAttribute('data-ry-sized');
    this.#clearPersist();
    // Re-apply any declarative [size]/[ratio] so reset returns to the authored
    // default (not always 50/50).
    this.#applySizing();
    this.#emitResize();
  };

  #emitResize(): void {
    const size = Math.round(this.#currentSize());
    this.emit('resize', { size, width: size });
  }

  // ── Persistence ──
  #persistKey(): string | null {
    const key = this.getAttribute('persist');
    return key ? `ry-split:${key}` : null;
  }

  #persist(): void {
    const key = this.#persistKey();
    if (!key) return;
    try { localStorage.setItem(key, String(Math.round(this.#currentSize()))); } catch { /* */ }
  }

  #restore(): void {
    const key = this.#persistKey();
    if (!key) return;
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        const px = parseFloat(saved);
        if (!isNaN(px)) {
          this.style.setProperty('--ry-split-size', `${px}px`);
          this.setAttribute('data-ry-sized', '');
        }
      }
    } catch { /* */ }
  }

  #clearPersist(): void {
    const key = this.#persistKey();
    if (!key) return;
    try { localStorage.removeItem(key); } catch { /* */ }
  }
}

customElements.define('ry-split', RySplit);
